import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import React from "react";
import { ReactComponent as SearchIcon } from "../assets/Icons/IconsSearch.svg";
import { ReactComponent as HomeSvg } from "../assets/Icons/Icon.svg";
import { ReactComponent as CartIcon } from "../assets/Icons/IconCart.svg";
import { ReactComponent as SupportIcon } from "../assets/Icons/IconSupport.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "@shared/redux/hooks";

const AWS_MARKETPLACE_URL = "https://aws.amazon.com/marketplace/seller-profile?id=seller-7ztsg5iwqd6z6";

interface RouteConfig {
  message: string;
  buttonLabel: string;
  targetRoute: string;
  icon: React.ReactNode;
  /** When set, replaces the default Kontrol empty-state body copy under the heading. */
  description?: string;
}

const DEFAULT_KONTROL_EMPTY_DESCRIPTION =
  "Your tenant is currently empty. To add new services, contact your account manager.";

const DEFAULT_KONTROL_EMPTY_ICON = (
  <SearchIcon className="h-10 w-10 text-semantic-text-secondary" />
);

const routeConfigs: Record<string, RouteConfig> = {
  "/kontrol/datacenter": {
    message: "You have no Datacenter Interconnect yet",
    buttonLabel: "Add New DCI",
    targetRoute: "/kontrol/add-dc",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/kontrol/port": {
    message: "You have no Ports yet",
    buttonLabel: "Add New Port",
    targetRoute: "/kontrol/add-port",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/kontrol/ipx": {
    message: "You have no Internet Connection yet",
    buttonLabel: "Add New Internet Connection",
    targetRoute: "/kontrol/add-ipx",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/kontrol/cloud-connectivity": {
    message: "You have no Cloud Connectivity yet",
    buttonLabel: "Add New Cloud Connectivity",
    targetRoute: "/kontrol/add-cc",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/kontrol/backbone": {
    message: "You have no Backbone yet",
    buttonLabel: "Add New Backbone",
    targetRoute: "/kontrol/add-backbone",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/kontrol/reports": {
    message: "You have no reports yet",
    buttonLabel: "Go to Kontrol",
    targetRoute: "/kontrol",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/kontrol/fwaas": {
    message: "You have no FWaaS services yet",
    buttonLabel: "Go to Kontrol",
    targetRoute: "/kontrol",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/kontrol/sdwan-hub": {
    message: "You have no SDWAN-Hub services yet",
    buttonLabel: "Go to Kontrol",
    targetRoute: "/kontrol",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/kontrol/alerts": {
    message: "No network alerts yet",
    description:
      "There are no active incidents right now. When incidents occur, they appear here; use the History tab to review past events.",
    buttonLabel: "Go to Kontrol",
    targetRoute: "/kontrol",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>,
  },
  "/kontrol/internet-ip-monitoring": {
    message: "No monitored IPs yet",
    description:
      "The IPSLA monitoring catalog is empty. Monitored IPs per backbone uplink will appear here once configured.",
    buttonLabel: "Go to Kontrol",
    targetRoute: "/kontrol",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>,
  },
  "/kontrol": {
    message: "You have no analytics data in your dashboard.",
    buttonLabel: "Add New Services",
    targetRoute: "/kontrol",
    icon: <HomeSvg className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/reflector-v2/management/clusters": {
    message: "You have no Clusters yet",
    buttonLabel: "Add New Cluster",
    targetRoute: "/reflector-v2/management/clusters/add",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/reflector-v2/management/bgp-reflectors": {
    message: "You have no Reflectors yet",
    buttonLabel: "Add New Reflector",
    targetRoute: "/reflector-v2/management/bgp-reflectors/add",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/reflector-v2/management/cloud-propagation": {
    message: "You have no Cloud Propagation Neighbors yet",
    buttonLabel: "Add New Cloud Propagation Neighbor",
    targetRoute: "/reflector-v2/management/cloud-propagation/add",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/reflector-v2/management/bgp": {
    message: "You have no BGP Neighbors yet",
    buttonLabel: "Add New BGP Neighbor",
    targetRoute: "/reflector-v2/management/bgp/add",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/reflector-v2/overview": {
    message: "You have no Data yet",
    buttonLabel: "Add New Data",
    targetRoute: "/reflector-v2/overview",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/reflector-v2/management/bgp-visibility": {
    message: "You have no BGP Configurations yet",
    buttonLabel: "Add New BGP Configuration",
    targetRoute: "/reflector-v2/management/bgp-visibility",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary"/>
  },
  "/account/tags-manager": {
    message: "No tags yet",
    buttonLabel: "Create tag",
    targetRoute: "/account/tags-manager/new",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary" />,
    description:
      "Tags help you classify and search across your resources. Create one to get started.",
  },
  "/account/notifications": {
    message: "No notification groups yet",
    buttonLabel: "New group",
    targetRoute: "/account/notifications/groups/new",
    icon: <SearchIcon className="h-10 w-10 text-semantic-text-secondary" />,
    description:
      "Create a group to choose who gets notified and which resources to watch.",
  },
};

function getRouteConfig(pathname: string): RouteConfig | null {
  if (routeConfigs[pathname]) return routeConfigs[pathname];
  const match = Object.keys(routeConfigs).find((key) =>
    pathname.startsWith(key)
  );
  return match ? routeConfigs[match] : null;
}

const PAYG_LICENSING_PATH = "/reflector-v2/management/licensing/payg";

export interface ReflectorManagementOnboardingEmptyStateProps {
  /** Main heading, e.g. "You have no Reflectors yet" */
  heading: string;
}

/**
 * Onboarding empty state for Reflector v2 management pages (reflectors, clusters, etc.):
 * PAYG steps, AWS Marketplace link, Licensing + Contact Sales CTAs.
 */
export const ReflectorManagementOnboardingEmptyState: React.FC<
  ReflectorManagementOnboardingEmptyStateProps
> = ({ heading }) => {
  const navigate = useNavigate();
  const userProfile = useAppSelector((state) => state.userProfile);

  const handleOpenDocs = () => {
    const base = process.env.REACT_APP_GITBOOK_URL || "https://docs.transitxchange.cloud";
    const token = userProfile.gitbookToken;
    const url = token ? `${base}?jwt_token=${token}` : base;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <GenericCard
          isCollapsible={false}
          variant="primary"
          bodyContent={
            <div className="flex flex-col items-center text-center space-y-4 p-6">
              <h3 className="text-base font-bold text-semantic-text-primary">
                {heading}
              </h3>

              <ol className="w-full space-y-2 text-left text-sm text-semantic-text-secondary list-decimal list-inside">
                <li>
                  Go to{" "}
                  <button
                    type="button"
                    className="text-semantic-text-brand underline hover:opacity-80"
                    onClick={() => navigate(PAYG_LICENSING_PATH)}
                  >
                    Licensing
                  </button>{" "}
                  and generate a PAYG token.
                </li>
                <li>
                  Deploy from{" "}
                  <a
                    href={AWS_MARKETPLACE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-semantic-text-brand underline hover:opacity-80"
                  >
                    AWS Marketplace
                  </a>
                  .
                </li>
                <li>Register your instance with the token.</li>
              </ol>

              <div className="flex w-full flex-col gap-2 pt-2">
                <GenericButton
                  label="Go to Licensing"
                  variant="brand"
                  size="md"
                  icon={<CartIcon className="h-4 w-4 text-white" />}
                  className="w-full justify-center"
                  onClick={() => navigate(PAYG_LICENSING_PATH)}
                />
                <GenericButton
                  label="Contact Sales (BYOL)"
                  variant="outlined"
                  size="md"
                  icon={<SupportIcon className="h-4 w-4" />}
                  className="w-full justify-center"
                  onClick={() => navigate("/support/request")}
                />
                <GenericButton
                  label="Documentation"
                  variant="outlined"
                  size="md"
                  icon={
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
                      />
                    </svg>
                  }
                  className="w-full justify-center"
                  onClick={handleOpenDocs}
                />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

/**
 * Empty state for Reflector v2 **Licensing** (paid licenses list): how to buy / activate
 * (PAYG token, AWS Marketplace, BYOL). Use when the tenant has no licenses yet.
 */
export const BuyLicensingEmptyState: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <GenericCard
          isCollapsible={false}
          variant="primary"
          bodyContent={
            <div className="flex flex-col items-center text-center space-y-4 p-6">
              <h3 className="text-base font-bold text-semantic-text-primary">
                You have no licenses yet
              </h3>

              <ol className="w-full space-y-2 text-left text-sm text-semantic-text-secondary list-decimal list-inside">
                <li>
                  Open{" "}
                  <button
                    type="button"
                    className="text-semantic-text-brand underline hover:opacity-80"
                    onClick={() => navigate(PAYG_LICENSING_PATH)}
                  >
                    PAYG licensing
                  </button>{" "}
                  to generate a token and buy usage-based licensing.
                </li>
                <li>
                  Or deploy from{" "}
                  <a
                    href={AWS_MARKETPLACE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-semantic-text-brand underline hover:opacity-80"
                  >
                    AWS Marketplace
                  </a>
                  .
                </li>
                <li>Register your instance with your license or PAYG token.</li>
              </ol>

              <div className="flex w-full flex-col gap-2 pt-2">
                <GenericButton
                  label="Buy licensing (PAYG)"
                  variant="brand"
                  size="md"
                  icon={<CartIcon className="h-4 w-4 text-white" />}
                  className="w-full justify-center"
                  onClick={() => navigate(PAYG_LICENSING_PATH)}
                />
                <GenericButton
                  label="Contact Sales (BYOL)"
                  variant="outlined"
                  size="md"
                  icon={<SupportIcon className="h-4 w-4" />}
                  className="w-full justify-center"
                  onClick={() => navigate("/support/request")}
                />
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

const ReflectorV2EmptyState: React.FC<{ config: RouteConfig | null }> = ({ config }) => (
  <ReflectorManagementOnboardingEmptyState
    heading={config ? config.message : "No items found"}
  />
);

const DefaultEmptyStateInner: React.FC<{
  config: RouteConfig | null;
  actionLabel?: string;
  onAction?: () => void;
}> = ({ config, actionLabel, onAction }) => (
  <div className="flex flex-col items-center justify-center text-center space-y-4 p-6">
    <div className="bg-semantic-bg-secondary inline-flex items-center justify-center border rounded-md p-2">
      {config?.icon ?? DEFAULT_KONTROL_EMPTY_ICON}
    </div>
    <h3 className="text-1xl font-bold">
      {config ? config.message : "No items found"}
    </h3>
    <p className="text-sm text-semantic-text-secondary">
      {config?.description ?? DEFAULT_KONTROL_EMPTY_DESCRIPTION}
    </p>
    {actionLabel && onAction && (
      <GenericButton
        label={actionLabel}
        variant="brand"
        size="md"
        onClick={onAction}
      />
    )}
  </div>
);

const DefaultEmptyState: React.FC<{
  config: RouteConfig | null;
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Compact variant: render just the centered message (no full-height
   * `min-h-[calc(100vh-10rem)]` wrapper, no own `GenericCard`) so the caller
   * can drop it inside an existing card — e.g. the Notification Groups table
   * card, where the empty state should sit where the table normally is.
   */
  inline?: boolean;
}> = ({ config, actionLabel, onAction, inline = false }) => {
  if (inline) {
    return (
      <DefaultEmptyStateInner
        config={config}
        actionLabel={actionLabel}
        onAction={onAction}
      />
    );
  }
  return (
    <div className="min-h-[calc(100vh-10rem)] flex items-center justify-center">
      <div className="w-1/2">
        <GenericCard
          isCollapsible={false}
          bodyContent={
            <DefaultEmptyStateInner
              config={config}
              actionLabel={actionLabel}
              onAction={onAction}
            />
          }
        />
      </div>
    </div>
  );
};

export interface EmptyItemsProps {
  /**
   * Optional call-to-action rendered inside the default empty state (e.g.
   * "New group"). When omitted the empty state stays text-only, so existing
   * usages are unchanged. Ignored on Reflector V2 onboarding empty states.
   */
  actionLabel?: string;
  onAction?: () => void;
  /**
   * Compact in-card variant: drops the full-height centering + own
   * `GenericCard` so the caller can render the empty state inside an existing
   * card. Ignored on Reflector V2 onboarding empty states. Defaults to the
   * full-page state so existing usages are unchanged.
   */
  inline?: boolean;
}

const EmptyItems: React.FC<EmptyItemsProps> = ({
  actionLabel,
  onAction,
  inline,
}) => {
  const location = useLocation();
  const currentConfig = getRouteConfig(location.pathname);
  const isReflectorV2Route = location.pathname.startsWith("/reflector-v2");

  if (isReflectorV2Route) {
    return <ReflectorV2EmptyState config={currentConfig} />;
  }

  return (
    <DefaultEmptyState
      config={currentConfig}
      actionLabel={actionLabel}
      onAction={onAction}
      inline={inline}
    />
  );
};

export default EmptyItems;